import React, { createContext, useContext, useReducer } from 'react';
// --- Types ---
export interface CRMData {
  loanOfficer: string;
  loanPartner: string;
  leadSource: string;
  campaign: string;
  applicationDate: string;
  transactionType: 'purchase' | 'refinance';
}
export interface GoalsData {
  selectedGoals: string[];
  cashOutAmount: string;
  cashOutPurposes: string[];
  customPurpose: string;
  otherGoal: string;
}
export interface Address {
  id: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  monthsAtAddress: string;
}
export interface Borrower {
  id: string;
  role: 'borrower' | 'co-borrower' | 'additional';
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  maritalStatus: string;
  spouseOwnership: boolean | null;
  participatesOnLoan: boolean | null;
  isTitleHolder: boolean | null;
  isVeteran: boolean | null;
  vaDisabilityPercent: string;
  vaDisabilityIncome: string;
  ssn: string;
  dob: string;
  creditScore: string;
  residencyHistory: Address[];
}
export interface TitleHolder {
  id: string;
  name: string;
  relationship: string;
}
export interface Note {
  id: string;
  timestamp: string;
  author: string;
  text: string;
}
export interface ApplicationState {
  crm: CRMData;
  goals: GoalsData;
  borrowers: Borrower[];
  additionalTitleHolders: TitleHolder[];
  // Stubs for future phases
  employmentByBorrower: Record<string, any[]>;
  additionalIncome: any[];
  debts: any[];
  assets: any[];
  subjectProperty: any;
  existingMortgage: any;
  additionalProperties: any[];
  creditEvents: any;
  notes: Note[];
}
// --- Initial State ---
const initialState: ApplicationState = {
  crm: {
    loanOfficer: 'Jordan Avery',
    loanPartner: 'Sam Rivera',
    leadSource: 'Web Lead',
    campaign: 'Spring Refi',
    applicationDate: new Date().toISOString().split('T')[0],
    transactionType: 'purchase'
  },
  goals: {
    selectedGoals: [],
    cashOutAmount: '',
    cashOutPurposes: [],
    customPurpose: '',
    otherGoal: ''
  },
  borrowers: [{
    id: 'b1',
    role: 'borrower',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    maritalStatus: '',
    spouseOwnership: null,
    participatesOnLoan: true,
    isTitleHolder: true,
    isVeteran: null,
    vaDisabilityPercent: '',
    vaDisabilityIncome: '',
    ssn: '',
    dob: '',
    creditScore: '',
    residencyHistory: []
  }],
  additionalTitleHolders: [],
  employmentByBorrower: {},
  additionalIncome: [],
  debts: [],
  assets: [],
  subjectProperty: {},
  existingMortgage: {},
  additionalProperties: [],
  creditEvents: {},
  notes: []
};
// --- Actions ---
type Action = {
  type: 'UPDATE_CRM';
  payload: Partial<CRMData>;
} | {
  type: 'UPDATE_GOALS';
  payload: Partial<GoalsData>;
} | {
  type: 'ADD_BORROWER';
  payload: Borrower;
} | {
  type: 'UPDATE_BORROWER';
  payload: {
    id: string;
    data: Partial<Borrower>;
  };
} | {
  type: 'REMOVE_BORROWER';
  payload: string;
} | {
  type: 'SWAP_BORROWERS';
  payload: {
    id1: string;
    id2: string;
  };
};
// --- Reducer ---
function applicationReducer(state: ApplicationState, action: Action): ApplicationState {
  switch (action.type) {
    case 'UPDATE_CRM':
      return {
        ...state,
        crm: {
          ...state.crm,
          ...action.payload
        }
      };
    case 'UPDATE_GOALS':
      return {
        ...state,
        goals: {
          ...state.goals,
          ...action.payload
        }
      };
    case 'ADD_BORROWER':
      return {
        ...state,
        borrowers: [...state.borrowers, action.payload]
      };
    case 'UPDATE_BORROWER':
      return {
        ...state,
        borrowers: state.borrowers.map(b => b.id === action.payload.id ? {
          ...b,
          ...action.payload.data
        } : b)
      };
    case 'REMOVE_BORROWER':
      return {
        ...state,
        borrowers: state.borrowers.filter(b => b.id !== action.payload)
      };
    case 'SWAP_BORROWERS':
      {
        const b1Index = state.borrowers.findIndex(b => b.id === action.payload.id1);
        const b2Index = state.borrowers.findIndex(b => b.id === action.payload.id2);
        if (b1Index === -1 || b2Index === -1) return state;
        const newBorrowers = [...state.borrowers];
        const tempRole = newBorrowers[b1Index].role;
        newBorrowers[b1Index] = {
          ...newBorrowers[b1Index],
          role: newBorrowers[b2Index].role
        };
        newBorrowers[b2Index] = {
          ...newBorrowers[b2Index],
          role: tempRole
        };
        // Swap positions in array
        const temp = newBorrowers[b1Index];
        newBorrowers[b1Index] = newBorrowers[b2Index];
        newBorrowers[b2Index] = temp;
        return {
          ...state,
          borrowers: newBorrowers
        };
      }
    default:
      return state;
  }
}
// --- Context & Provider ---
const ApplicationContext = createContext<{
  state: ApplicationState;
  dispatch: React.Dispatch<Action>;
} | null>(null);
export function ApplicationProvider({
  children
}: {
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(applicationReducer, initialState);
  return <ApplicationContext.Provider value={{
    state,
    dispatch
  }} data-id="element-690">
      {children}
    </ApplicationContext.Provider>;
}
export function useApplicationState() {
  const context = useContext(ApplicationContext);
  if (!context) throw new Error('useApplicationState must be used within ApplicationProvider');
  return context;
}
// --- Selectors ---
export function calculateCompletion(state: ApplicationState): number {
  let total = 0;
  let completed = 0;
  // Goals (1 point)
  total += 1;
  if (state.goals.selectedGoals.length > 0) completed += 1;
  // Borrowers (5 points each)
  state.borrowers.forEach(b => {
    total += 5;
    if (b.firstName && b.lastName) completed += 1;
    if (b.email) completed += 1;
    if (b.phone) completed += 1;
    if (b.ssn) completed += 1;
    if (b.dob) completed += 1;
  });
  return Math.round(completed / total * 100) || 0;
}